import { div, esc, kr, marginPill, nf1, num, pct, qty, signed } from '../calc/format.js';
import { CFG, WEEKDAYS, isAlternativePayment, paymentLabel } from '../calc/model.js';
import { hasExactNumber, searchProducts } from '../calc/search.js';
import { NO_COST_TEXT, NO_COST_TITLE, el, emptyRow, headCell, sortRows } from './dom.js';
import { helpButton } from './help.js';
import { state } from './state.js';

export function renderWeekdayTable(s, has){
  const t = el('tWeekday');
  if(!has){ t.innerHTML = emptyRow(6,'Inga kvitton i urvalet'); return; }

  let html = `<thead><tr><th>Veckodag</th><th class="num">Omsättning</th><th class="num">Andel</th>
    <th class="num">Antal köp</th><th class="num">Snittköp</th><th class="num">Artiklar/köp</th></tr></thead><tbody>`;

  let shown = 0;
  WEEKDAYS.forEach((wd,i) => {
    const w = s.byWeekday.get(i);
    if(!w || w.tx === 0) return;   // veckodagar utan försäljning utelämnas
    shown++;
    html += `<tr>
      <td class="name">${esc(wd)}</td>
      <td class="num">${esc(kr(w.revenue))}</td>
      <td class="num">${esc(pct(div(w.revenue, s.revenue)*100))}</td>
      <td class="num">${esc(num(w.tx))}</td>
      <td class="num">${esc(kr(div(w.revenue, w.tx)))}</td>
      <td class="num">${esc(nf1.format(div(w.units, w.tx)).replace('.',','))}</td>
    </tr>`;
  });
  if(!shown) html += emptyRow(6,'Ingen försäljning i perioden');
  t.innerHTML = html + '</tbody>';
}

export function renderSplit(s, has){
  const t = el('tSplit');
  if(!has){ t.innerHTML = emptyRow(7,'Inga kvitton i urvalet'); return; }

  // En typ utan försäljning får ingen rad: en butik utan tjänster ska inte
  // se "Tjänster 0 kr". En enstaka rad på 0 kr räknas inte som försäljning.
  const rows = [['Varor', s.product], ['Tjänster', s.service]]
    .filter(([, b]) => b.grossRevenue || b.revenue || b.revenueEx || b.profit);
  el('splitSub').textContent = (rows.length > 1 ? 'Varor och tjänster redovisas var för sig. ' : '') +
    'Kolumnen längst till höger visar hur stor del av försäljningen som saknar inköpspris, och därmed hur säker marginalen är';

  let html = `<thead><tr><th>Typ</th><th class="num">Omsättning</th><th class="num">Andel</th>
    <th class="num">Exkl. moms</th><th class="num">Bruttovinst</th><th class="num">Marginal</th>
    <th class="num">Utan inköpspris</th></tr></thead><tbody>`;

  for(const [label, b] of rows){
    const blind = b.noCostShare;
    html += `<tr>
      <td class="name">${esc(label)}</td>
      <td class="num">${esc(kr(b.revenue))}</td>
      <td class="num">${esc(pct(div(b.revenue, s.revenue)*100))}</td>
      <td class="num">${esc(kr(b.revenueEx))}</td>
      <td class="num">${state.hasCost ? esc(kr(b.profit)) : '–'}</td>
      <td class="num">${state.hasCost && b.revenueEx ? marginPill(b.margin) : '–'}</td>
      <td class="num">${
        !b.grossRevenue ? '–'
        : blind >= 1 ? `<span class="pill ${blind >= 50 ? 'pill-low' : 'pill-mid'}" title="Så stor del av försäljningen saknar registrerat inköpspris. Marginalen ovan är därför osäker.">${esc(pct(blind))}</span>`
        // Under 1 % är marginalen säker, men andelen ska ändå stå som den är:
        // "0 %" här och "0,2 %" i totalen vore motsägelsefullt
        : `<span class="pill pill-good">${esc(blind > 0 ? pct(blind) : '0 %')}</span>`
      }</td>
    </tr>`;
  }
  html += `<tr>
      <td class="name"><b>Totalt</b></td>
      <td class="num"><b>${esc(kr(s.revenue))}</b></td>
      <td class="num">100,0 %</td>
      <td class="num"><b>${esc(kr(s.revenueEx))}</b></td>
      <td class="num"><b>${state.hasCost ? esc(kr(s.profit)) : '–'}</b></td>
      <td class="num">${state.hasCost ? marginPill(s.margin) : '–'}</td>
      <td class="num">${esc(pct(s.noCostRevenueShare))}</td>
    </tr></tbody>`;

  // Poster som ligger utanför försäljningen ovan
  const extras = [];
  if(s.giftCardCount){
    extras.push(`Utfärdade presentkort: <b>${esc(num(s.giftCardCount))} st</b> till ett värde av <b>${esc(kr(s.giftCardFace))}</b>`);
  }
  if(s.bonusChecks) extras.push(`Inlösta bonuscheckar: <b>${esc(kr(s.bonusChecks))}</b>`);
  if(Math.abs(s.roundOff) >= 0.5) extras.push(`Öresavrundning: <b>${esc(kr(s.roundOff))}</b>`);

  html += extras.length
    ? `<tfoot><tr><td colspan="7" style="color:var(--ink-soft);font-size:13px;padding-top:14px;border-bottom:none">
       Utanför försäljningen ovan: ${extras.join(' · ')}. Ett presentkort blir försäljning först när det löses in. Då syns det som betalsätt under Butik &amp; kassör.
       </td></tr></tfoot>`
    : '';

  t.innerHTML = html;
}

export function renderProducts(){
  const s = state.stats;
  const t = el('tProducts');

  if(!state.view.length){
    el('cProduct').textContent = '';
    t.innerHTML = emptyRow(8,'Inga kvitton i urvalet','Välj en annan period eller ett bredare urval.');
    return;
  }

  let rows = [...s.products.values()].map(p => ({
    name:p.name, articleNumber:p.articleNumber,
    units:p.units, revenue:p.revenue, revenueEx:p.revenueEx,
    profit:p.profit, discount:p.discount,
    margin: p.revenueEx ? p.profit/p.revenueEx*100 : 0,
    share: div(p.revenue, s.revenue)*100,
    isService:p.isService, noCost:p.noCost
  }));

  const all = rows;
  const found = searchProducts(rows, state.productQuery);
  rows = found.items;

  const total = rows.length;
  rows = sortRows(rows, state.productSort).slice(0, CFG.MAX_ROWS);

  let count = total > CFG.MAX_ROWS
    ? `Visar ${num(CFG.MAX_ROWS)} av ${num(total)} artiklar`
    : `${num(total)} ${total === 1 ? 'artikel' : 'artiklar'}`;
  if(found.missing.length){
    // Ett nummer kan finnas i artikelregistret men inte ha sålts i urvalet
    count += ` · inte sålda i urvalet: ${found.missing.join(', ')}`;
  }else if(total > 1 && hasExactNumber(all, state.productQuery)){
    const nr = state.productQuery.trim();
    count += ` · skriv ${nr}, för bara artikelnummer ${nr}`;
  }
  el('cProduct').textContent = count;

  if(!total){
    t.innerHTML = found.query && found.query.mode === 'list'
      ? emptyRow(8,'Inget av artikelnumren såldes i urvalet','Kontrollera numren, eller välj en längre period eller ett bredare urval.')
      : emptyRow(8,'Ingen artikel matchar sökningen','Prova ett annat sökord.');
    return;
  }

  const S = state.productSort;
  let html = '<thead><tr><th class="rank"></th>' +
    headCell('Artikel','name', S) +
    headCell('Antal','units', S,'num') +
    headCell('Omsättning','revenue', S,'num') +
    headCell('Andel','share', S,'num') +
    headCell('Rabatt','discount', S,'num') +
    headCell('Bruttovinst','profit', S,'num') +
    headCell('Marginal','margin', S,'num') +
    '</tr></thead><tbody>';

  rows.forEach((r,i) => {
    html += `<tr>
      <td class="rank">${i+1}</td>
      <td class="name">${esc(r.name)}${r.articleNumber ? `<small>${esc(r.articleNumber)}${r.isService ? ' · tjänst' : ''}</small>` : (r.isService ? '<small>tjänst</small>' : '')}</td>
      <td class="num">${signed(r.units, qty)}</td>
      <td class="num">${signed(r.revenue, kr)}</td>
      <td class="num">${esc(pct(r.share))}</td>
      <td class="num">${r.discount ? esc(kr(r.discount)) : '–'}</td>
      <td class="num">${state.hasCost ? signed(r.profit, kr) : '–'}</td>
      <td class="num">${
        !r.revenueEx ? '–'
        : r.noCost ? `<span class="pill pill-mid" title="Inköpspris saknas i artikelregistret, marginalen går inte att räkna ut">Inköpspris saknas</span>`
        : marginPill(r.margin)
      }</td>
    </tr>`;
  });

  t.innerHTML = html + '</tbody>';
}

export function marginSource(){
  const s = state.stats;
  switch(el('mGroup').value){
    case 'supplier':  return s.suppliers;
    case 'groupFull': return s.groupFull;
    case 'product':   return s.products;
    default:          return s.group1;
  }
}

export function renderMargin(){
  const s = state.stats;
  const t = el('tMargin');

  el('btnExportMargin').disabled = !state.hasCost;
  if(!state.hasCost){ t.innerHTML = emptyRow(7, NO_COST_TITLE, NO_COST_TEXT); return; }
  if(!state.view.length){
    t.innerHTML = emptyRow(7,'Inga kvitton i urvalet');
    return;
  }

  const src = marginSource();
  const isProduct = el('mGroup').value === 'product';

  let rows = [...src.entries()].map(([k,v]) => ({
    name: isProduct ? v.name : k,
    units: v.units,
    revenue: v.revenue,
    revenueEx: v.revenueEx,
    profit: v.profit,
    margin: v.revenueEx ? v.profit/v.revenueEx*100 : 0,
    share: div(v.profit, s.profit)*100,
    articles: isProduct ? 1 : (v.items ? v.items.size : 0)
  }));

  rows = sortRows(rows, state.marginSort).slice(0, CFG.MAX_ROWS);

  const S = state.marginSort;
  let html = '<thead><tr><th class="rank"></th>' +
    headCell(isProduct ? 'Artikel' : 'Grupp','name', S) +
    (isProduct ? '' : headCell('Artiklar','articles', S,'num')) +
    headCell('Antal sålda','units', S,'num') +
    headCell('Omsättning','revenue', S,'num') +
    headCell('Bruttovinst','profit', S,'num') +
    headCell('Andel av vinst','share', S,'num') +
    headCell('Marginal','margin', S,'num') +
    '</tr></thead><tbody>';

  rows.forEach((r,i) => {
    html += `<tr>
      <td class="rank">${i+1}</td>
      <td class="name">${esc(r.name)}</td>
      ${isProduct ? '' : `<td class="num">${esc(num(r.articles))}</td>`}
      <td class="num">${signed(r.units, qty)}</td>
      <td class="num">${signed(r.revenue, kr)}</td>
      <td class="num">${signed(r.profit, kr)}</td>
      <td class="num">${esc(pct(r.share))}</td>
      <td class="num">${r.revenueEx ? marginPill(r.margin) : '–'}</td>
    </tr>`;
  });

  t.innerHTML = html + '</tbody>';
}

export function renderStores(s, has){
  const card = el('storesCard');
  const t = el('tStores');

  // Med bara en butik säger tabellen inget som översikten inte redan visar.
  // Samma resonemang som att butiksfiltret stängs av vid en enda butik.
  if(has && s.stores.size <= 1){
    card.style.display = 'none';
    return;
  }
  card.style.display = '';

  if(!has){ t.innerHTML = emptyRow(8,'Inga kvitton i urvalet'); return; }

  const rows = [...s.stores.entries()]
    .map(([k,v]) => ({name: k == null ? 'Okänd butik' : (state.storeLabels.get(k) || 'Butik ' + k), ...v}))
    .sort((a,b) => b.revenue - a.revenue);

  let html = `<thead><tr><th class="rank"></th><th>Butik</th><th class="num">Omsättning</th>
    <th class="num">Andel</th><th class="num">Antal köp</th><th class="num">Snittköp</th>
    <th class="num">Artiklar/köp</th><th class="num">Marginal</th></tr></thead><tbody>`;

  rows.forEach((r,i) => {
    html += `<tr>
      <td class="rank">${i+1}</td>
      <td class="name">${esc(r.name)}</td>
      <td class="num">${esc(kr(r.revenue))}</td>
      <td class="num">${esc(pct(div(r.revenue, s.revenue)*100))}</td>
      <td class="num">${esc(num(r.tx))}</td>
      <td class="num">${esc(kr(div(r.revenue, r.tx)))}</td>
      <td class="num">${esc(nf1.format(div(r.units, r.tx)).replace('.',','))}</td>
      <td class="num">${state.hasCost && r.revenueEx ? marginPill(div(r.profit, r.revenueEx)*100) : '–'}</td>
    </tr>`;
  });
  t.innerHTML = html + '</tbody>';
}

export function renderCashiers(s, has){
  const t = el('tCashiers');
  if(!has){ t.innerHTML = emptyRow(6,'Inga kvitton i urvalet'); return; }

  const rows = [...s.cashiers.entries()]
    .map(([k,v]) => ({name:k, ...v}))
    .sort((a,b) => b.revenue - a.revenue);

  let html = `<thead><tr><th class="rank"></th><th>Kassör</th><th class="num">Omsättning</th>
    <th class="num">Antal köp</th><th class="num">Snittköp</th><th class="num">Artiklar/köp</th>
    <th class="num">Marginal</th></tr></thead><tbody>`;

  rows.forEach((r,i) => {
    html += `<tr>
      <td class="rank">${i+1}</td>
      <td class="name">${esc(r.name)}</td>
      <td class="num">${esc(kr(r.revenue))}</td>
      <td class="num">${esc(num(r.tx))}</td>
      <td class="num">${esc(kr(div(r.revenue, r.tx)))}</td>
      <td class="num">${esc(nf1.format(div(r.units, r.tx)).replace('.',','))}</td>
      <td class="num">${state.hasCost && r.revenueEx ? marginPill(div(r.profit, r.revenueEx)*100) : '–'}</td>
    </tr>`;
  });
  t.innerHTML = html + '</tbody>';
}

/** Så många korttyper visas var för sig; resten summeras på en rad. */
const CARD_ROWS = 12;

export function renderCards(s, has){
  const card = el('cardsCard');
  card.hidden = !state.terminal.any;
  if(card.hidden) return;
  const t = el('tCards');
  if(!has){ t.innerHTML = emptyRow(4,'Inga kvitton i urvalet'); return; }
  if(state.lineFiltered){
    t.innerHTML = emptyRow(4,'Kortbetalningar visas inte när du väljer artikelgrupp eller leverantör',
      'Kunden betalar hela kvittot på en gång, så betalningen går inte att dela upp per artikel.');
    return;
  }
  if(!s.cardCount){ t.innerHTML = emptyRow(4,'Inga kortbetalningar i urvalet'); return; }

  // Samma korttyp kan stå med olika skiftläge; den vanligaste stavningen visas
  const spelling = e => [...e.spellings].sort((a,b) => b[1] - a[1])[0][0];
  const named = [], special = [];
  for(const [key, e] of s.cards){
    if(key === null)    special.push({name:'Utan terminalkvitto', note:'Kortbetalningen har inget kvitto från terminalen', ...e});
    else if(key === '') special.push({name:'Okänt kort', note:'Terminalens kvitto anger ingen korttyp som går att läsa', ...e});
    else named.push({name: spelling(e), ...e});
  }
  named.sort((a,b) => b.amount - a.amount);
  const shown = named.length > CARD_ROWS + 1 ? named.slice(0, CARD_ROWS) : named;
  const rest = named.slice(shown.length);
  if(rest.length){
    shown.push({name:`Övriga korttyper (${num(rest.length)} st)`,
      amount: rest.reduce((a,r) => a + r.amount, 0), count: rest.reduce((a,r) => a + r.count, 0)});
  }
  const rows = [...shown, ...special];
  const total = rows.reduce((a,r) => a + r.amount, 0);

  let html = `<thead><tr><th>Kort</th><th class="num">Belopp</th><th class="num">Andel</th>
    <th class="num">Antal betalningar</th></tr></thead><tbody>`;
  for(const r of rows){
    html += `<tr>
      <td class="name">${esc(r.name)}${r.note ? `<small>${esc(r.note)}</small>` : ''}</td>
      <td class="num">${esc(kr(r.amount))}</td>
      <td class="num">${esc(pct(div(r.amount, total)*100))}</td>
      <td class="num">${esc(num(r.count))}</td>
    </tr>`;
  }
  html += '</tbody>';

  // Kontaktlöst och personlig kod räknas på betalningar med terminalkvitto,
  // och visas bara när filen har sådana markeringar alls
  const base = s.cardWithTerminal;
  const parts = [];
  if(state.terminal.contactless && base) parts.push(`Kontaktlöst: <b>${esc(pct(div(s.cardContactless, base)*100))}</b>`);
  if(state.terminal.pin && base)         parts.push(`med personlig kod: <b>${esc(pct(div(s.cardPin, base)*100))}</b>`);
  if(parts.length){
    const of = base === s.cardCount ? 'av kortbetalningarna' : `av de ${num(base)} kortbetalningarna med terminalkvitto`;
    html += `<tfoot><tr><td colspan="4" style="color:var(--ink-soft);font-size:13px;padding-top:14px;border-bottom:none">
      ${(j => j.charAt(0).toUpperCase() + j.slice(1))(parts.join(', '))} ${esc(of)}. Ett kontaktlöst köp kan också kräva kod, så andelarna kan tillsammans bli mer än 100 %.
      </td></tr></tfoot>`;
  }
  t.innerHTML = html;
}

export function renderPayments(s, has){
  const t = el('tPayments');
  if(!has){ t.innerHTML = emptyRow(4,'Inga kvitton i urvalet'); return; }
  if(state.lineFiltered){
    t.innerHTML = emptyRow(4,'Betalsätt visas inte när du väljer artikelgrupp eller leverantör',
      'Kunden betalar hela kvittot på en gång, så betalningen går inte att dela upp per artikel.');
    return;
  }

  const rows = [...s.payments.entries()]
    .map(([k,v]) => ({name: paymentLabel(k), help: isAlternativePayment(k), ...v}))
    .sort((a,b) => b.amount - a.amount);
  const total = rows.reduce((a,r) => a + r.amount, 0);

  let html = `<thead><tr><th>Betalsätt</th><th class="num">Belopp</th><th class="num">Andel</th>
    <th class="num">Antal betalningar</th></tr></thead><tbody>`;
  rows.forEach(r => {
    html += `<tr>
      <td class="name">${esc(r.name)}${r.help ? helpButton('alternativt') : ''}</td>
      <td class="num">${esc(kr(r.amount))}</td>
      <td class="num">${esc(pct(div(r.amount, total)*100))}</td>
      <td class="num">${esc(num(r.count))}</td>
    </tr>`;
  });
  t.innerHTML = html + '</tbody>';
}

export function renderTopProfit(s, has){
  const t = el('tTopProfit');
  if(!state.hasCost){ t.innerHTML = emptyRow(4, NO_COST_TITLE, NO_COST_TEXT); return; }
  if(!has){ t.innerHTML = emptyRow(4,'Inga kvitton i urvalet'); return; }

  const rows = [...s.products.values()]
    .filter(p => p.profit > 0)
    .sort((a,b) => b.profit - a.profit)
    .slice(0,10);

  if(!rows.length){ t.innerHTML = emptyRow(4,'Ingen artikel med positiv vinst'); return; }

  let html = `<thead><tr><th class="rank"></th><th>Artikel</th><th class="num">Bruttovinst</th>
    <th class="num">Andel av vinst</th></tr></thead><tbody>`;
  rows.forEach((p,i) => {
    html += `<tr><td class="rank">${i+1}</td>
      <td class="name">${esc(p.name)}</td>
      <td class="num">${esc(kr(p.profit))}</td>
      <td class="num">${esc(pct(div(p.profit, s.profit)*100))}</td></tr>`;
  });
  t.innerHTML = html + '</tbody>';
}

export function renderLowMargin(s, has){
  const t = el('tLowMargin');
  if(!state.hasCost){ t.innerHTML = emptyRow(4, NO_COST_TITLE, NO_COST_TEXT); return; }
  if(!has){ t.innerHTML = emptyRow(4,'Inga kvitton i urvalet'); return; }

  const rows = [...s.products.values()]
    .filter(p => p.units >= CFG.MIN_QTY_LOW_MARGIN && p.revenueEx > 0)
    .map(p => ({...p, margin: p.profit/p.revenueEx*100}))
    .sort((a,b) => a.margin - b.margin)
    .slice(0,10);

  if(!rows.length){
    t.innerHTML = emptyRow(4,'Inget att visa',
      `Ingen artikel har sålts minst ${CFG.MIN_QTY_LOW_MARGIN} gånger i den här perioden.`);
    return;
  }

  let html = `<thead><tr><th class="rank"></th><th>Artikel</th><th class="num">Antal</th>
    <th class="num">Omsättning</th><th class="num">Marginal</th></tr></thead><tbody>`;
  rows.forEach((p,i) => {
    html += `<tr><td class="rank">${i+1}</td>
      <td class="name">${esc(p.name)}</td>
      <td class="num">${esc(qty(p.units))}</td>
      <td class="num">${esc(kr(p.revenue))}</td>
      <td class="num">${marginPill(p.margin)}</td></tr>`;
  });
  t.innerHTML = html + '</tbody>';
}

export function renderReturns(s, has){
  const t = el('tReturns');
  if(!has){ t.innerHTML = emptyRow(4,'Inga kvitton i urvalet'); return; }

  const rows = [...s.returns.values()].sort((a,b) => a.value - b.value).slice(0,15);

  if(!rows.length){
    t.innerHTML = emptyRow(4,'Inga returer i perioden','Ingen kvittorad har negativt antal.');
    return;
  }

  let html = `<thead><tr><th>Artikel</th><th class="num">Returnerat antal</th>
    <th class="num">Belopp</th><th class="num">Antal tillfällen</th></tr></thead><tbody>`;
  rows.forEach(r => {
    html += `<tr>
      <td class="name">${esc(r.name)}</td>
      <td class="num neg">${esc(qty(r.units))}</td>
      <td class="num neg">${esc(kr(r.value))}</td>
      <td class="num">${esc(num(r.count))}</td>
    </tr>`;
  });
  html += `</tbody><tfoot><tr>
    <td class="name"><b>Totalt</b></td>
    <td class="num neg"><b>${esc(qty(s.returnUnits))}</b></td>
    <td class="num neg"><b>${esc(kr(s.returnValue))}</b></td>
    <td class="num"><b>${esc(num(s.returnReceipts))} kvitton</b></td>
  </tr></tfoot>`;
  t.innerHTML = html;
}
